import { useEffect, useMemo, useState } from 'react';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { authenticatedFetch } from '../../lib/api';
import { useToast } from '../../components/shared/Toast';
import { PageSkeleton } from '@euroasiann/shared/ui';

export function ApiDocsPage() {
  const { showToast } = useToast();
  const [spec, setSpec] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authenticatedFetch('/api/v1/docs/openapi.json');
        const json = await res.json();
        if (!res.ok) {
          throw new Error(json.error || 'Failed to load API documentation');
        }
        if (!cancelled) setSpec(json);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load API documentation';
        if (!cancelled) {
          setError(message);
          showToast(message, 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showToast]);

  const requestInterceptor = useMemo(
    () => (req: { headers?: Record<string, string>; url?: string }) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        req.headers = req.headers || {};
        req.headers.Authorization = `Bearer ${token}`;
      }
      return req;
    },
    []
  );

  if (loading) {
    return (
      <div className="p-8">
        <PageSkeleton variant="page" />
      </div>
    );
  }

  if (error || !spec) {
    return (
      <div className="p-8">
        <p className="text-sm text-red-400">{error || 'Documentation unavailable'}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-8 pt-6 pb-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]">
        <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]">API Documentation</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Swagger UI for the Ship Span ERP backend. Your tech portal session token is attached to
          &quot;Try it out&quot; requests automatically.
        </p>
      </div>
      <div className="flex-1 min-h-0 overflow-auto api-docs-swagger">
        <SwaggerUI
          spec={spec}
          requestInterceptor={requestInterceptor}
          persistAuthorization
          docExpansion="list"
          defaultModelsExpandDepth={1}
        />
      </div>
    </div>
  );
}

export default ApiDocsPage;
